import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  Catalogue,
  CatalogueSettings,
  CatalogueShareLink,
  CatalogueSharingMode,
} from "../../../types/catalogue.types";
import notifications from "../../../common/Components/Notifications/Notification";
import ShareLinkExpiryFields from "../ShareLinkExpiryFields";
import ShareCountryPhoneFields from "../ShareCountryPhoneFields";
import ShareRecipientNameField from "../ShareRecipientNameField";
import ShareAccessPasswordOnce from "../ShareAccessPasswordOnce";
import ShareLinkReady from "../ShareLinkReady";
import ShareLinkRow from "../ShareLinkRow";
import SharePartyMatch from "../SharePartyMatch";
import CatalogueSharingModeField from "../CatalogueSharingModeField";
import { isCatalogueResellerSharingEnabled } from "../../../helpers/catalogueResellerSharingEnabled";
import {
  createCatalogueShareLink,
  disableCatalogueShareLink,
  emailCatalogueShareInvite,
  extendCatalogueShareLinkExpiry,
  setCatalogueShareLinkExpiry,
  listCatalogueShareLinks,
  listCatalogueLinkExtensionRequests,
  approveCatalogueLinkExtension,
  declineCatalogueLinkExtension,
  setCatalogueShareLinkAccessMode,
  setCatalogueShareLinkPriceTier,
  setCatalogueShareLinkAccessPassword,
  setCatalogueShareLinkViewerGate,
} from "../catalogueSecurityApi";
import { isCatalogueViewerGateEnabled } from "../../../helpers/catalogueViewerGateEnabled";
import { VIEWER_GATE_SHORT } from "../catalogueViewerGate";
import type { CatalogueLinkExtensionRequest, CatalogueViewerGate } from "../../../types/catalogue.types";
import {
  DEFAULT_SHARE_COUNTRY_ISO,
  isIndiaShareCountry,
  isPlausibleSharePhone,
  inviteCountryIso,
  isValidShareEmail,
  partyPhoneToShareFields,
} from "../catalogueShareCountries";
import { shareLinkPublicUrl } from "../shareLinkPublicUrl";
import { copyText } from "../copyText";
import {
  DEFAULT_SHARE_EXPIRY_AMOUNT,
  DEFAULT_SHARE_EXPIRY_UNIT,
  shareLinkExpiryPayload,
  type ShareExpiryUnit,
  shareLinkExpiryLabel,
} from "../shareLinkExpiry";

interface SettingsTabProps {
  catalogue: Catalogue;
  onGoToPricing?: () => void;
  onSharingModeChange?: (mode: CatalogueSharingMode) => void;
}

const DEFAULT_SETTINGS: CatalogueSettings = {
  password: "",
  watermarkEnabled: false,
  watermarkText: "HKP • Confidential",
  accessExpiry: "7d",
  screenshotProtection: "watermark",
  allowDownload: false,
  trackViews: true
};

type ShareLinkRow = CatalogueShareLink;

type SecurityEventRow = {
  id: string;
  eventType: string;
  linkId?: string;
  actorRole?: string;
  createdAt?: string;
  clientIp?: string;
  metadata?: { detail?: string; success?: boolean };
};

const SettingsTab: React.FC<SettingsTabProps> = ({ catalogue, onGoToPricing, onSharingModeChange }) => {
  const [settings, setSettings] = useState<CatalogueSettings>(DEFAULT_SETTINGS);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [links, setLinks] = useState<ShareLinkRow[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [newLinkName, setNewLinkName] = useState("");
  const [newLinkPhone, setNewLinkPhone] = useState("");
  const [newLinkCountry, setNewLinkCountry] = useState(DEFAULT_SHARE_COUNTRY_ISO);
  const [newLinkEmail, setNewLinkEmail] = useState("");
  const [newLinkPassword, setNewLinkPassword] = useState("");
  const [newLinkGeneratePassword, setNewLinkGeneratePassword] = useState(false);
  const [newLinkSendEmail, setNewLinkSendEmail] = useState(false);
  const [newLinkExpiryAmount, setNewLinkExpiryAmount] = useState<number>(DEFAULT_SHARE_EXPIRY_AMOUNT);
  const [newLinkExpiryUnit, setNewLinkExpiryUnit] = useState<ShareExpiryUnit>(DEFAULT_SHARE_EXPIRY_UNIT);
  const [revealedPassword, setRevealedPassword] = useState("");
  const [revealedPasswordEmailed, setRevealedPasswordEmailed] = useState(false);
  const [passwordModalLink, setPasswordModalLink] = useState<ShareLinkRow | null>(null);
  const [rotatePassword, setRotatePassword] = useState("");
  const [rotateGenerate, setRotateGenerate] = useState(true);
  const [rotateSendEmail, setRotateSendEmail] = useState(true);
  const [rotateBusy, setRotateBusy] = useState(false);
  const [events, setEvents] = useState<SecurityEventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const newLinkNonIndia = !isIndiaShareCountry(newLinkCountry);
  // This form makes links with the catalogue's default access. Open links need no name or number.
  const newLinkOpen = isCatalogueResellerSharingEnabled() && catalogue.sharingMode === "OPEN";

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || ""}`,
    };
  };

  const mapExpiryToDays = (value: CatalogueSettings["accessExpiry"]) => {
    switch (value) {
      case "24h":
        return 1;
      case "3d":
        return 3;
      case "7d":
        return 7;
      case "30d":
        return 30;
      default:
        return 0;
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      if (!catalogue?.id) return;

      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_SERVERURL}/catalogue-security/${catalogue.id}/status`,
          { headers: getAuthHeaders() }
        );
        const result = await response.json();
        if (!response.ok || !result?.success) {
          notifications({
            title: "Settings",
            message: result?.message || "Could not load security status.",
            type: "warning",
          });
          return;
        }

        const data = result.data || {};
        const remainingMs = Number(data.remainingTime);
        let accessExpiry: CatalogueSettings["accessExpiry"] = "never";
        if (data.expiryDate && remainingMs !== -2) {
          const days = remainingMs > 0 ? remainingMs / (24 * 60 * 60 * 1000) : 0;
          if (remainingMs <= 0) accessExpiry = "24h";
          else if (days > 20) accessExpiry = "30d";
          else if (days > 5) accessExpiry = "7d";
          else if (days > 2) accessExpiry = "3d";
          else accessExpiry = "24h";
        }

        setSettings({
          ...DEFAULT_SETTINGS,
          password: "",
          watermarkEnabled: data.watermarkEnabled === true,
          watermarkText: typeof data.watermarkText === "string" ? data.watermarkText : "",
          accessExpiry,
          screenshotProtection:
            data.screenshotProtection === "strict" ||
            data.screenshotProtection === "watermark" ||
            data.screenshotProtection === "off"
              ? data.screenshotProtection
              : data.allowScreenshots === false
                ? "strict"
                : "off",
          allowDownload: Boolean(data.allowTextSelection),
          trackViews: true,
        });
      } catch {
        notifications({
          title: "Settings",
          message: "Could not load security status.",
          type: "warning",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [catalogue?.id]);

  const loadLinks = async () => {
    if (!catalogue?.id) return;
    try {
      setLinksLoading(true);
      const [json, asks] = await Promise.all([
        listCatalogueShareLinks(catalogue.id),
        // Requests are extra: when the feature is off or the call fails, the links still show.
        listCatalogueLinkExtensionRequests(catalogue.id).catch(() => null),
      ]);
      if (json?.success && Array.isArray(json?.data)) {
        setLinks(json.data);
      }
      setExtensionAsks(asks?.success && Array.isArray(asks.data) ? asks.data : []);
    } finally {
      setLinksLoading(false);
    }
  };

  const [extensionAsks, setExtensionAsks] = useState<CatalogueLinkExtensionRequest[]>([]);
  const [extensionBusyId, setExtensionBusyId] = useState<string | null>(null);
  const decideExtension = async (ask: CatalogueLinkExtensionRequest, days?: 7 | 30 | 90) => {
    if (!catalogue?.id || extensionBusyId) return;
    setExtensionBusyId(ask.id);
    try {
      const json = await (days
        ? approveCatalogueLinkExtension(catalogue.id, ask.id, days)
        : declineCatalogueLinkExtension(catalogue.id, ask.id)
      ).catch(() => ({ success: false, message: "Could not reach the server. Check the connection and try again." }));
      if (json?.success) {
        notifications({
          title: days ? "Link reopened" : "Declined",
          message: days
            ? `${ask.requesterName}'s link works again for ${days} days. Same link, nothing to resend.`
            : `${ask.requesterName} can ask again tomorrow.`,
          type: "success",
        });
      } else {
        notifications({ title: "Error", message: json?.message || "Could not save this decision.", type: "danger" });
      }
      loadLinks();
    } finally {
      setExtensionBusyId(null);
    }
  };

  // The link just created, shown under the form so it can be copied right away.
  const [readyLink, setReadyLink] = useState<{
    url: string;
    name: string;
    phone: string;
    countryIso: string;
    emailed: boolean;
    open: boolean;
  } | null>(null);

  const showOncePassword = (password?: string, emailed?: boolean) => {
    setRevealedPassword(password || "");
    setRevealedPasswordEmailed(Boolean(emailed));
  };

  useEffect(() => {
    loadLinks();
  }, [catalogue?.id]);

  const loadSecurityEvents = async () => {
    if (!catalogue?.id) return;
    try {
      setEventsLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_SERVERURL}/catalogue-security/${catalogue.id}/security-events?limit=100`,
        { headers: getAuthHeaders() }
      );
      const json = await res.json();
      if (res.ok && json?.success && Array.isArray(json?.data)) {
        setEvents(json.data);
      }
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    loadSecurityEvents();
  }, [catalogue?.id]);

  const createLink = async () => {
    if (!catalogue?.id) return;
    if (!newLinkName.trim() && !newLinkOpen) {
      notifications({
        title: "Missing name",
        message: "Recipient name is required.",
        type: "warning",
      });
      return;
    }
    if (!(newLinkOpen && !newLinkPhone.trim()) && !isPlausibleSharePhone(newLinkPhone, newLinkCountry)) {
      notifications({
        title: "Mobile required",
        message: newLinkNonIndia
          ? "Enter the client's local mobile for the selected country."
          : "Enter the 10-digit mobile number that should be allowed to open this link.",
        type: "warning",
      });
      return;
    }
    const emailRaw = newLinkEmail.trim();
    if (emailRaw && !isValidShareEmail(emailRaw)) {
      notifications({
        title: "Email",
        message: "Enter a valid email, or leave it blank.",
        type: "warning",
      });
      return;
    }
    const willGenerate =
      !newLinkOpen && (newLinkGeneratePassword || (newLinkNonIndia && !newLinkPassword.trim()));
    if (!newLinkOpen && newLinkNonIndia && !willGenerate && !newLinkPassword.trim()) {
      notifications({
        title: "Password required",
        message: "Generate a password or type one for overseas invites.",
        type: "warning",
      });
      return;
    }
    const expiry = shareLinkExpiryPayload(newLinkExpiryAmount, newLinkExpiryUnit);
    if (!expiry) {
      notifications({
        title: "Expiry",
        message: "Enter a duration of at least 1 minute, hour, or day.",
        type: "warning",
      });
      return;
    }
    const json = await createCatalogueShareLink(catalogue.id, {
      recipientName: newLinkName.trim(),
      recipientPhone: newLinkPhone.trim(),
      ...(newLinkOpen ? { accessMode: "OPEN" as const } : {}),
      recipientCountryCode: newLinkCountry,
      recipientEmail: emailRaw || undefined,
      sendInviteEmail: Boolean(emailRaw) && newLinkSendEmail,
      generateAccessPassword: willGenerate,
      linkPassword: willGenerate ? undefined : newLinkPassword.trim() || undefined,
      expiryAmount: expiry.expiryAmount,
      expiryUnit: expiry.expiryUnit,
    });
    if (json?.success) {
      const emailed = Boolean(json.data?.inviteEmailSent);
      showOncePassword(json.data?.accessPassword, emailed);
      if (json.data?.linkToken) {
        setReadyLink({
          url: shareLinkPublicUrl({
            catalogueId: catalogue.id,
            linkToken: json.data.linkToken,
            publicUrl: json.data.publicUrl,
          }),
          name: newLinkName.trim(),
          phone: newLinkPhone.trim(),
          countryIso: newLinkCountry,
          emailed,
          open: json.data.accessMode === "OPEN",
        });
      }
      notifications({
        title: emailed ? "Invite emailed" : "Link created",
        message: emailed ? "Link created and emailed." : "Link created. Copy it below and send it.",
        type: "success",
      });
      setNewLinkName("");
      setNewLinkPhone("");
      setNewLinkEmail("");
      setNewLinkPassword("");
      setNewLinkGeneratePassword(false);
      setNewLinkSendEmail(false);
      setNewLinkExpiryAmount(DEFAULT_SHARE_EXPIRY_AMOUNT);
      setNewLinkExpiryUnit(DEFAULT_SHARE_EXPIRY_UNIT);
      setSettings((prev) => ({ ...prev, password: "" }));
      loadLinks();
    } else {
      notifications({ title: "Error", message: json?.message || "Could not create link.", type: "danger" });
    }
  };

  const [copiedLinkId, setCopiedLinkId] = useState("");
  const copyShareLink = async (url: string, row: ShareLinkRow) => {
    if (await copyText(url)) {
      setCopiedLinkId(row.id);
      window.setTimeout(() => setCopiedLinkId((id) => (id === row.id ? "" : id)), 1800);
      notifications({ title: "Copied", message: `Link for ${row.recipientName || "the buyer"} copied.`, type: "success" });
      return;
    }
    // Clipboard blocked here: show the link so it can be copied or sent by hand.
    const fields = partyPhoneToShareFields(row.recipientPhone);
    setReadyLink({
      url,
      name: row.recipientName || "",
      phone: fields.phone,
      countryIso: inviteCountryIso(row.recipientCountryCode) || fields.countryIso,
      emailed: false,
      open: row.accessMode === "OPEN",
    });
    window.requestAnimationFrame(() =>
      document.querySelector(".hkp-link-ready")?.scrollIntoView({ block: "center", behavior: "smooth" })
    );
  };

  const disableLink = async (linkId: string) => {
    if (!catalogue?.id) return;
    if (!window.confirm("Disable this link now? Sessions using it will stop immediately.")) return;
    const json = await disableCatalogueShareLink(catalogue.id, linkId);
    if (json?.success) {
      notifications({ title: "Done", message: "Link disabled.", type: "success" });
      loadLinks();
      loadSecurityEvents();
    } else {
      notifications({ title: "Error", message: json?.message || "Could not disable link.", type: "danger" });
    }
  };

  const extendLink = async (linkId: string) => {
    if (!catalogue?.id) return;
    const json = await extendCatalogueShareLinkExpiry(catalogue.id, linkId, 7);
    if (json?.success) {
      notifications({ title: "Extended", message: "Expiry extended by 7 days.", type: "success" });
      loadLinks();
    } else {
      notifications({ title: "Error", message: json?.message || "Could not extend expiry.", type: "danger" });
    }
  };

  // One link's Manage panel open at a time.
  const [manageId, setManageId] = useState<string | null>(null);
  const [expiryBusy, setExpiryBusy] = useState(false);
  const saveExpiry = async (link: ShareLinkRow, amount: number, unit: ShareExpiryUnit): Promise<boolean> => {
    if (!catalogue?.id || expiryBusy) return false;
    const payload = shareLinkExpiryPayload(amount, unit);
    if (!payload) {
      notifications({ title: "Expiry", message: "Enter at least 1 minute, hour or day.", type: "warning" });
      return false;
    }
    setExpiryBusy(true);
    try {
      // A dropped connection is a toast, never a crash.
      const json = await setCatalogueShareLinkExpiry(catalogue.id, link.id, payload).catch(() => ({
        success: false,
        message: "Could not reach the server. Check the connection and try again.",
      }));
      if (json?.success) {
        notifications({
          title: "Expiry",
          message: `${link.recipientName || "This link"} now expires in ${shareLinkExpiryLabel(payload.expiryAmount, payload.expiryUnit)}.`,
          type: "success",
        });
        loadLinks();
        return true;
      }
      notifications({ title: "Error", message: json?.message || "Could not change this link's expiry.", type: "danger" });
      return false;
    } finally {
      setExpiryBusy(false);
    }
  };

  const [tierBusyId, setTierBusyId] = useState<string | null>(null);
  const changeLinkTier = async (link: ShareLinkRow, tier: string) => {
    if (!catalogue?.id || tierBusyId) return;
    setTierBusyId(link.id);
    try {
      const json = await setCatalogueShareLinkPriceTier(catalogue.id, link.id, tier);
      if (json?.success) {
        notifications({
          title: "Prices",
          message: `${link.recipientName}'s link now shows ${tier ? `${tier} prices` : "default prices"}. Other links are unchanged.`,
          type: "success",
        });
        loadLinks();
      } else {
        notifications({ title: "Error", message: json?.message || "Could not change this link's prices.", type: "danger" });
      }
    } finally {
      setTierBusyId(null);
    }
  };

  const [gateBusyId, setGateBusyId] = useState<string | null>(null);
  /** What one Open link asks each viewer before it opens. */
  const changeViewerGate = async (link: ShareLinkRow, gate: CatalogueViewerGate) => {
    if (!catalogue?.id || gateBusyId) return;
    setGateBusyId(link.id);
    try {
      const json = await setCatalogueShareLinkViewerGate(catalogue.id, link.id, gate).catch(() => ({
        success: false,
        message: "Could not reach the server. Check the connection and try again.",
      }));
      if (json?.success) {
        setLinks((prev) => prev.map((row) => (row.id === link.id ? { ...row, viewerGate: gate } : row)));
        notifications({
          title: "Who is viewing",
          message: `${link.recipientName || "This link"} now ${gate === "NONE" ? "opens straight away" : VIEWER_GATE_SHORT[gate]}.`,
          type: "success",
        });
      } else {
        notifications({ title: "Error", message: json?.message || "Could not change this link.", type: "danger" });
      }
    } finally {
      setGateBusyId(null);
    }
  };

  const [accessBusyId, setAccessBusyId] = useState<string | null>(null);
  const switchLinkAccess = async (link: ShareLinkRow) => {
    if (!catalogue?.id || accessBusyId) return;
    const next = link.accessMode === "OPEN" ? "EXCLUSIVE" : "OPEN";
    if (
      next === "EXCLUSIVE" &&
      !window.confirm(`${link.recipientName} will need a code from now on. Anyone browsing this link now is signed out.`)
    ) {
      return;
    }
    setAccessBusyId(link.id);
    try {
      const json = await setCatalogueShareLinkAccessMode(catalogue.id, link.id, next);
      if (json?.success) {
        // No SMS OTP for this number: the server made a password, shown once like on a new link.
        const password = json.data?.accessPassword;
        if (password) showOncePassword(password, false);
        notifications({
          title: next === "OPEN" ? "Open" : "Exclusive",
          message:
            next === "OPEN"
              ? `${link.recipientName}'s link opens without a code. Other links are unchanged.`
              : password
                ? `${link.recipientName}'s link needs a password now. Copy it below and send it to them.`
                : `${link.recipientName}'s link needs a code now. Other links are unchanged.`,
          type: "success",
        });
        loadLinks();
      } else {
        notifications({ title: "Error", message: json?.message || "Could not change this link.", type: "danger" });
      }
    } finally {
      setAccessBusyId(null);
    }
  };

  const emailInviteAgain = async (link: ShareLinkRow) => {
    if (!catalogue?.id || !link.recipientEmail) return;
    const json = await emailCatalogueShareInvite(catalogue.id, link.id);
    if (json?.success && json.data?.inviteEmailSent !== false) {
      notifications({ title: "Emailed", message: json.message || "Invite emailed to the client.", type: "success" });
    } else {
      notifications({
        title: "Email not sent",
        message: json?.message || "Copy the link and send it manually.",
        type: "warning",
      });
    }
  };

  const submitRotatePassword = async () => {
    if (!catalogue?.id || !passwordModalLink) return;
    const willGenerate = rotateGenerate || !rotatePassword.trim();
    if (!willGenerate && !rotatePassword.trim()) {
      notifications({
        title: "Password",
        message: "Type a password or choose Generate password.",
        type: "warning",
      });
      return;
    }
    try {
      setRotateBusy(true);
      const json = await setCatalogueShareLinkAccessPassword(catalogue.id, passwordModalLink.id, {
        generateAccessPassword: willGenerate,
        linkPassword: willGenerate ? undefined : rotatePassword.trim(),
        sendInviteEmail: Boolean(passwordModalLink.recipientEmail) && rotateSendEmail,
      });
      if (json?.success) {
        const emailed = Boolean(json.data?.inviteEmailSent);
        showOncePassword(json.data?.accessPassword, emailed);
        notifications({
          title: emailed ? "Password emailed" : "Copy password now",
          message: json.message || "Password set. It is shown only once.",
          type: emailed ? "success" : "warning",
        });
        setPasswordModalLink(null);
        setRotatePassword("");
        loadLinks();
      } else {
        notifications({ title: "Error", message: json?.message || "Could not set password.", type: "danger" });
      }
    } finally {
      setRotateBusy(false);
    }
  };

  const asIst = (v?: string) =>
    v
      ? new Date(v).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  const handleSettingChange = (key: keyof CatalogueSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    if (!catalogue?.id) return;
    try {
      setIsSaving(true);

      const securityResponse = await fetch(
        `${process.env.REACT_APP_SERVERURL}/catalogue-security/${catalogue.id}/security-settings`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            ...(settings.password?.trim() ? { password: settings.password.trim() } : {}),
            requirePassword: Boolean(settings.password?.trim()) || settings.screenshotProtection === "strict",
            allowScreenshots: settings.screenshotProtection !== "strict",
            allowTextSelection: settings.allowDownload,
            maxViews: -1,
            screenshotProtection: settings.screenshotProtection,
            watermarkEnabled: settings.watermarkEnabled,
            watermarkText: settings.watermarkText.trim(),
          }),
        }
      );
      const securityResult = await securityResponse.json();
      if (!securityResponse.ok || !securityResult?.success) {
        throw new Error(securityResult?.message || "Failed to update security settings");
      }

      const days = mapExpiryToDays(settings.accessExpiry);
      if (days > 0) {
        await fetch(
          `${process.env.REACT_APP_SERVERURL}/catalogue-security/${catalogue.id}/extend-expiry`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ days }),
          }
        );
      }

      notifications({
        title: "Success",
        message: "Catalogue settings updated successfully",
        type: "success",
      });
      setSettings((prev) => ({ ...prev, password: "" }));
    } catch (error: any) {
      notifications({
        title: "Error",
        message: error?.message || "Failed to save settings",
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm("Are you sure you want to reset all settings to default values?")) {
      setSettings(DEFAULT_SETTINGS);
    }
  };

  return (
    <div>
      <h3 className="catalogue-tab-heading">Settings</h3>
      {isLoading && <p className="text-muted">Loading security settings...</p>}
      {isCatalogueResellerSharingEnabled() && catalogue.id && !catalogue.systemManaged ? (
        <CatalogueSharingModeField
          catalogueId={catalogue.id}
          value={catalogue.sharingMode}
          onChange={onSharingModeChange}
        />
      ) : null}

      <Row>
        <Col lg={12} className="mb-4">
          <Card>
            <CardHeader style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
              <h6 className="catalogue-panel-title">Shared Links Registry (IST)</h6>
            </CardHeader>
            <CardBody>
              <Row className="g-2 mb-3">
                <Col md={4}>
                  <Label className="fw-bold" for="settings-share-name">
                    {newLinkOpen ? "Link name (optional)" : "Recipient name *"}
                  </Label>
                  <ShareRecipientNameField
                    id="settings-share-name"
                    value={newLinkName}
                    onChange={setNewLinkName}
                    onPick={(party) => {
                      setNewLinkName(party.name);
                      if (party.phone) {
                        setNewLinkPhone(party.phone);
                        setNewLinkCountry(party.countryIso);
                        if (!isIndiaShareCountry(party.countryIso)) {
                          setNewLinkGeneratePassword(true);
                        }
                      }
                      setNewLinkEmail(party.email);
                      setNewLinkSendEmail(party.email.trim().length > 0);
                    }}
                  />
                </Col>
                <ShareCountryPhoneFields
                  countryIso={newLinkCountry}
                  phone={newLinkPhone}
                  onCountryChange={(iso) => {
                    setNewLinkCountry(iso);
                    if (!isIndiaShareCountry(iso)) {
                      setNewLinkGeneratePassword(true);
                    }
                  }}
                  onPhoneChange={setNewLinkPhone}
                  countryId="settings-share-country"
                  phoneId="settings-share-phone"
                  phoneOptional={newLinkOpen}
                  mdCountry={3}
                  mdPhone={5}
                />
                <Col md={12}>
                  <SharePartyMatch
                    phone={newLinkPhone}
                    countryIso={newLinkCountry}
                    name={newLinkName}
                    onUseParty={(party) => {
                      setNewLinkName(party.name);
                      if (party.phone) {
                        setNewLinkPhone(party.phone);
                        setNewLinkCountry(party.countryIso);
                      }
                      if (party.email) {
                        setNewLinkEmail(party.email);
                        setNewLinkSendEmail(true);
                      }
                    }}
                  />
                </Col>
              </Row>
              <Row className="g-2 mb-3">
                <Col md={5}>
                  <Label className="fw-bold" for="settings-share-email">
                    Email (optional)
                  </Label>
                  <Input
                    id="settings-share-email"
                    type="email"
                    value={newLinkEmail}
                    onChange={(e) => {
                      setNewLinkEmail(e.target.value);
                      setNewLinkSendEmail(e.target.value.trim().length > 0);
                    }}
                    placeholder="client@company.com"
                  />
                </Col>
                <Col md={4}>
                  <Label className="fw-bold" for="settings-share-password">Password</Label>
                  <Input
                    id="settings-share-password"
                    type="text"
                    autoComplete="off"
                    value={newLinkPassword}
                    disabled={newLinkGeneratePassword}
                    onChange={(e) => setNewLinkPassword(e.target.value)}
                    placeholder={newLinkNonIndia ? "Required unless generated" : "Optional"}
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <FormGroup check className="mb-2">
                    <Input
                      type="checkbox"
                      id="settings-generate-password"
                      checked={newLinkGeneratePassword}
                      onChange={(e) => setNewLinkGeneratePassword(e.target.checked)}
                    />
                    <Label check for="settings-generate-password">
                      Generate password
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  id="settings-send-invite-email"
                  checked={newLinkSendEmail && Boolean(newLinkEmail.trim())}
                  disabled={!newLinkEmail.trim()}
                  onChange={(e) => setNewLinkSendEmail(e.target.checked)}
                />
                <Label check for="settings-send-invite-email">
                  Email the link and password to the client
                </Label>
              </FormGroup>
              <Row className="g-2 mb-3">
                <ShareLinkExpiryFields
                  amount={newLinkExpiryAmount}
                  unit={newLinkExpiryUnit}
                  onAmountChange={setNewLinkExpiryAmount}
                  onUnitChange={setNewLinkExpiryUnit}
                />
              </Row>
              <small className="text-muted d-block mb-2">
                Link expiry can be minutes, hours, or days (minimum 1). Default is 30 days.
              </small>

              <Row className="g-2 mb-3">
                <Col md={12} className="d-flex align-items-end">
                  <Button onClick={createLink} style={{ background: "#0071E3", borderColor: "#0071E3" }}>
                    Create Share Link
                  </Button>
                </Col>
              </Row>

              <small className="text-muted d-block mb-3">
                Catalogues stay closed. Buyers unlock with the invited mobile plus SMS OTP, email OTP, or the password you set.
              </small>

              {readyLink ? (
                <ShareLinkReady
                  url={readyLink.url}
                  recipientName={readyLink.name}
                  phone={readyLink.phone}
                  countryIso={readyLink.countryIso}
                  catalogueName={catalogue.name}
                  emailed={readyLink.emailed}
                  open={readyLink.open}
                  onDismiss={() => setReadyLink(null)}
                />
              ) : null}

              <ShareAccessPasswordOnce password={revealedPassword} emailed={revealedPasswordEmailed} />

              <div className="hkp-link-list" data-testid="share-link-list">
                {linksLoading ? (
                  <p className="text-muted small mb-0">Loading links...</p>
                ) : links.length === 0 ? (
                  <p className="text-muted small mb-0">No links created yet.</p>
                ) : (
                  [...links]
                    .sort((a, b) => {
                      const rank = (link: ShareLinkRow) => {
                        if (link.disabled) return 2;
                        if (link.expiresAt && new Date(link.expiresAt).getTime() < Date.now()) {
                          return 1;
                        }
                        return 0;
                      };
                      return rank(a) - rank(b);
                    })
                    .map((l) => (
                      <ShareLinkRow
                        key={l.id}
                        link={l}
                        url={shareLinkPublicUrl({ catalogueId: catalogue.id, linkToken: l.linkToken, publicUrl: l.publicUrl })}
                        copied={copiedLinkId === l.id}
                        expanded={manageId === l.id}
                        resellerOn={isCatalogueResellerSharingEnabled()}
                        viewerGateOn={isCatalogueViewerGateEnabled()}
                        busy={{
                          access: accessBusyId === l.id,
                          gate: gateBusyId === l.id,
                          tier: tierBusyId === l.id,
                          expiry: expiryBusy,
                        }}
                        formatDate={asIst}
                        extensionRequest={extensionAsks.find((a) => a.linkId === l.id)}
                        extensionBusy={Boolean(extensionBusyId)}
                        onApproveExtension={(days) => {
                          const ask = extensionAsks.find((a) => a.linkId === l.id);
                          if (ask) void decideExtension(ask, days);
                        }}
                        onDeclineExtension={() => {
                          const ask = extensionAsks.find((a) => a.linkId === l.id);
                          if (ask) void decideExtension(ask);
                        }}
                        onCopy={() =>
                          void copyShareLink(
                            shareLinkPublicUrl({ catalogueId: catalogue.id, linkToken: l.linkToken, publicUrl: l.publicUrl }),
                            l
                          )
                        }
                        onToggle={() => setManageId((id) => (id === l.id ? null : l.id))}
                        onSwitchAccess={() => void switchLinkAccess(l)}
                        onGate={(gate) => void changeViewerGate(l, gate)}
                        onTier={(tier) => void changeLinkTier(l, tier)}
                        onPassword={() => {
                          setPasswordModalLink(l);
                          setRotateGenerate(true);
                          setRotatePassword("");
                          setRotateSendEmail(Boolean(l.recipientEmail));
                        }}
                        onEmail={() => emailInviteAgain(l)}
                        onExtend={() => extendLink(l.id)}
                        onSetExpiry={(amount, unit) => saveExpiry(l, amount, unit)}
                        onDisable={() => disableLink(l.id)}
                      />
                    ))
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={Boolean(passwordModalLink)} toggle={() => setPasswordModalLink(null)}>
        <ModalHeader toggle={() => setPasswordModalLink(null)}>
          {passwordModalLink?.requirePassword ? "Rotate access password" : "Set access password"}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            The new password is shown only once. {passwordModalLink?.recipientEmail
              ? "You can email it with the invite link (not a forgotten-password reset)."
              : "This invite has no email — copy the password and send it yourself."}
          </p>
          <FormGroup>
            <Label for="rotate-link-password">Password</Label>
            <Input
              id="rotate-link-password"
              type="text"
              autoComplete="off"
              value={rotatePassword}
              disabled={rotateGenerate}
              onChange={(e) => setRotatePassword(e.target.value)}
              placeholder="Type a password, or generate one"
            />
          </FormGroup>
          <FormGroup check className="mb-2">
            <Input
              type="checkbox"
              id="rotate-generate"
              checked={rotateGenerate}
              onChange={(e) => setRotateGenerate(e.target.checked)}
            />
            <Label check for="rotate-generate">
              Generate password
            </Label>
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              id="rotate-send-email"
              checked={rotateSendEmail && Boolean(passwordModalLink?.recipientEmail)}
              disabled={!passwordModalLink?.recipientEmail}
              onChange={(e) => setRotateSendEmail(e.target.checked)}
            />
            <Label check for="rotate-send-email">
              Email the link and this password to the client
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPasswordModalLink(null)}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "#0071E3", borderColor: "#0071E3" }}
            onClick={submitRotatePassword}
            disabled={rotateBusy}
          >
            {rotateBusy ? "Saving…" : "Save password"}
          </Button>
        </ModalFooter>
      </Modal>

      <Row>
        {/* Access & Security */}
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <CardHeader 
              style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
            >
              <h6 className="mb-0">🔐 Access & Security</h6>
            </CardHeader>
            <CardBody>
              {/* Access Expiry */}
              <FormGroup className="mb-3">
                <Label for="expiry" className="fw-bold">Access Expiry</Label>
                <Input
                  type="select"
                  id="expiry"
                  value={settings.accessExpiry}
                  onChange={(e) => handleSettingChange('accessExpiry', e.target.value)}
                >
                  <option value="24h">24 hours</option>
                  <option value="3d">3 days</option>
                  <option value="7d">7 days</option>
                  <option value="30d">30 days</option>
                  <option value="never">Never expire</option>
                </Input>
                <small className="text-muted">
                  How long shared links remain active
                </small>
              </FormGroup>

              {/* Screenshot Protection */}
              <FormGroup className="mb-3">
                <Label for="screenshot" className="fw-bold">Screenshot Protection</Label>
                <Input
                  type="select"
                  id="screenshot"
                  value={settings.screenshotProtection}
                  onChange={(e) => handleSettingChange('screenshotProtection', e.target.value)}
                >
                  <option value="strict">Block screenshots + hidden mark</option>
                  <option value="watermark">Hidden mark only</option>
                  <option value="off">Off</option>
                </Input>
                <small className="text-muted">
                  {settings.screenshotProtection === "strict"
                    ? "Buyers can't right-click, save, print or copy images, and the page hides on screenshot shortcuts or app switching. Every photo also carries an invisible mark for its link; check a leak under Activity."
                    : settings.screenshotProtection === "watermark"
                      ? "Every photo carries an invisible mark for its link. Nothing changes on screen; if a screenshot leaks, check it under Activity to see whose link it came from."
                      : "No protection: photos are served as they are."}
                </small>
              </FormGroup>

              {/* Download Permission */}
              <FormGroup className="mb-3">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="allowDownload"
                    checked={settings.allowDownload}
                    onChange={(e) => handleSettingChange('allowDownload', e.target.checked)}
                  />
                  <Label className="form-check-label fw-bold" for="allowDownload">
                    Allow Image Downloads
                  </Label>
                </div>
                <small className="text-muted">
                  Buyers get a "Save photo" button. The saved photo is stamped with their name, the date and the link code.
                </small>
              </FormGroup>

              {/* View Tracking */}
              <FormGroup>
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="trackViews"
                    checked={settings.trackViews}
                    onChange={(e) => handleSettingChange('trackViews', e.target.checked)}
                  />
                  <Label className="form-check-label fw-bold" for="trackViews">
                    Track Client Views
                  </Label>
                </div>
                <small className="text-muted">
                  Monitor client activity and engagement
                </small>
              </FormGroup>
            </CardBody>
          </Card>
        </Col>

        {/* Watermark & Branding */}
        <Col lg={6} className="mb-4">
          <Card className="h-100">
            <CardHeader 
              style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
            >
              <h6 className="mb-0">🎨 Watermark & Branding</h6>
            </CardHeader>
            <CardBody>
              {/* Watermark Toggle */}
              <FormGroup className="mb-3">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="watermarkEnabled"
                    checked={settings.watermarkEnabled}
                    onChange={(e) => handleSettingChange('watermarkEnabled', e.target.checked)}
                  />
                  <Label className="form-check-label fw-bold" for="watermarkEnabled">
                    Show a visible watermark
                  </Label>
                </div>
                <small className="text-muted">
                  Off by default. When on, buyers see their name tiled faintly over the page. Saved photos are always stamped.
                </small>
              </FormGroup>

              {/* Watermark Text */}
              {settings.watermarkEnabled && (
                <FormGroup className="mb-3">
                  <Label for="watermarkText" className="fw-bold">Watermark Text</Label>
                  <Input
                    type="text"
                    id="watermarkText"
                    value={settings.watermarkText}
                    onChange={(e) => handleSettingChange('watermarkText', e.target.value)}
                    placeholder="HKP • Confidential"
                    maxLength={60}
                  />
                  <small className="text-muted">
                    Shown with the buyer's name and a link code. A reseller's clients never see this line.
                  </small>
                </FormGroup>
              )}

              {/* Watermark Preview */}
              {settings.watermarkEnabled && (
                <div className="mb-3">
                  <Label className="fw-bold">Watermark Preview</Label>
                  <div 
                    className="border rounded p-4 text-center position-relative"
                    style={{ 
                      minHeight: "120px",
                      backgroundColor: "#f8f9fa",
                      backgroundImage: "linear-gradient(45deg, #e9ecef 25%, transparent 25%), linear-gradient(-45deg, #e9ecef 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e9ecef 75%), linear-gradient(-45deg, transparent 75%, #e9ecef 75%)",
                      backgroundSize: "20px 20px",
                      backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                    }}
                  >
                    <div 
                      className="position-absolute text-muted"
                      style={{
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%) rotate(-15deg)",
                        fontSize: "14px",
                        fontWeight: "bold",
                        opacity: 0.7,
                        border: "1px solid #6c757d",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: "rgba(255, 255, 255, 0.8)"
                      }}
                    >
                      {settings.watermarkText || "Sample Watermark"}
                    </div>
                    <small className="text-muted">Sample product image with watermark</small>
                  </div>
                </div>
              )}

              {/* Logo Upload */}
              <FormGroup>
                <Label className="fw-bold">Custom Logo (Optional)</Label>
                <Input type="file" accept="image/*" />
                <small className="text-muted">
                  Upload your logo to use in watermark (PNG recommended)
                </small>
              </FormGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        
      </Row>

      <Row>
        <Col lg={12} className="mb-4">
          <Card>
            <CardHeader style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
              <h6 className="catalogue-panel-title">Security Events (Studio + Super Admin)</h6>
            </CardHeader>
            <CardBody>
              <div className="d-flex justify-content-end mb-2">
                <Button size="sm" outline color="secondary" onClick={loadSecurityEvents}>
                  Refresh
                </Button>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Time (IST)</th>
                      <th>Event</th>
                      <th>Link ID</th>
                      <th>Actor role</th>
                      <th>IP</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventsLoading ? (
                      <tr>
                        <td colSpan={6} className="text-muted">Loading events...</td>
                      </tr>
                    ) : events.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-muted">No security events yet.</td>
                      </tr>
                    ) : (
                      events.map((e) => (
                        <tr key={e.id}>
                          <td>{asIst(e.createdAt)}</td>
                          <td>{e.eventType || "—"}</td>
                          <td><code>{e.linkId || "—"}</code></td>
                          <td>{e.actorRole || "viewer"}</td>
                          <td>{e.clientIp || "—"}</td>
                          <td>{e.metadata?.detail || (e.metadata?.success ? "Success" : "—")}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Pricing Management Quick Link */}
      <Row>
        <Col lg={6} className="mb-4">
          <Card>
            <CardHeader 
              style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
            >
              <h6 className="catalogue-panel-title">Pricing Management</h6>
            </CardHeader>
            <CardBody>
              <p className="text-muted mb-3">
                Audience tier, bulk % and CSV live on Pricing. Set a piece’s catalogue price next to its image on Products.
              </p>
              <div className="d-flex gap-2">
                <Button 
                  outline
                  style={{ 
                    borderColor: "#0071E3", 
                    color: "#0071E3"
                  }}
                  onClick={onGoToPricing}
                  disabled={!onGoToPricing}
                >
                  Go to Pricing tools
                </Button>
                <Button 
                  outline
                  style={{ 
                    borderColor: "#0071E3", 
                    color: "#0071E3"
                  }}
                >
                  Pricing History
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Notification Settings */}
        <Col lg={6} className="mb-4">
          <Card>
            <CardHeader 
              style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
            >
              <h6 className="mb-0">🔔 Notification Settings</h6>
            </CardHeader>
            <CardBody>
              <FormGroup className="mb-3">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="notifyViews"
                    defaultChecked
                  />
                  <Label className="form-check-label" for="notifyViews">
                    Notify on catalogue views
                  </Label>
                </div>
              </FormGroup>

              <FormGroup className="mb-3">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="notifyInquiries"
                    defaultChecked
                  />
                  <Label className="form-check-label" for="notifyInquiries">
                    Notify on new inquiries
                  </Label>
                </div>
              </FormGroup>

              <FormGroup className="mb-3">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="notifyExpiry"
                    defaultChecked
                  />
                  <Label className="form-check-label" for="notifyExpiry">
                    Notify before link expiry
                  </Label>
                </div>
              </FormGroup>

              <FormGroup>
                <Label for="notificationEmail">Notification Email</Label>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Catalogue inquiry and order emails go to the studio email on your
                  organisation profile, plus any extra addresses listed there — not
                  to every studio user.
                </p>
              </FormGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row>
        <Col xs={12}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="fw-bold mb-1">Save Settings</h6>
                  <small className="text-muted">
                    Changes will be applied immediately to all shared links
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    outline
                    color="secondary"
                    onClick={handleResetSettings}
                  >
                    🔄 Reset to Default
                  </Button>
                  <Button 
                    style={{ 
                      backgroundColor: "#0071E3", 
                      borderColor: "#0071E3"
                    }}
                    onClick={handleSaveSettings}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save All Settings"}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SettingsTab;
